<template>
  <!-- `!` on the h1, p and a utilities: lithium's unlayered element rules would win otherwise -->
  <div
    data-testid="oc-confirmation-step"
    class="confirmation-step step-inner-padded tw:flex tw:justify-center tw:font-[family-name:Inter,sans-serif]">
    <ConfettiBurst v-if="showConfetti" />
    <div
      class="confirmation-card tw:w-150 tw:max-w-full tw:rounded-md tw:border tw:border-solid tw:border-border tw:bg-surface tw:shadow-card">
      <div class="tw:px-8 tw:pt-6 tw:pb-8">
        <div class="hero tw:relative tw:mx-auto tw:mb-5 tw:h-30 tw:w-50" aria-hidden="true">
          <span
            v-for="dot in heroDots"
            :key="dot.key"
            class="hero-dot tw:absolute tw:rounded-full"
            :class="dot.colorClass"
            :style="dot.style" />
          <div
            class="hero-icon tw:absolute tw:top-1/2 tw:left-1/2 tw:flex tw:size-20 tw:-translate-1/2 tw:items-center tw:justify-center tw:rounded-full tw:bg-primary-lt tw:text-primary">
            <IconSparkles :size="32" :stroke-width="1.75" />
          </div>
        </div>

        <h1 class="confirmation-title tw:mt-0! tw:mb-2! tw:text-center tw:text-30! tw:font-bold! tw:text-gray-900!">
          {{ $t('order_creation.confirmation.title') }}
        </h1>
        <p
          data-testid="oc-confirmation-subtitle"
          class="confirmation-subtitle tw:mt-0! tw:mb-6! tw:text-center tw:text-14! tw:text-muted">
          {{ subtitle }}
        </p>

        <div data-testid="oc-confirmation-summary" class="summary tw:rounded-md tw:bg-gray-50 tw:p-5">
          <div class="summary-row tw:flex tw:items-start tw:justify-between tw:gap-6 tw:py-1.5 tw:text-14">
            <span class="summary-label tw:shrink-0 tw:text-13 tw:text-muted">{{
              $t('order_creation.confirmation.order_id')
            }}</span>
            <span
              data-testid="oc-confirmation-order-id"
              class="summary-value tw:min-w-0 tw:text-right tw:font-medium tw:wrap-anywhere tw:text-gray-900"
              >#{{ order.id }}</span
            >
          </div>
          <div class="summary-row tw:flex tw:items-start tw:justify-between tw:gap-6 tw:py-1.5 tw:text-14">
            <span class="summary-label tw:shrink-0 tw:text-13 tw:text-muted">{{
              $t('order_creation.confirmation.job_title')
            }}</span>
            <span class="summary-value tw:min-w-0 tw:text-right tw:font-medium tw:wrap-anywhere tw:text-gray-900">{{
              order.title
            }}</span>
          </div>
          <div class="summary-row tw:flex tw:items-start tw:justify-between tw:gap-6 tw:py-1.5 tw:text-14">
            <span class="summary-label tw:shrink-0 tw:text-13 tw:text-muted">{{
              $t('order_creation.confirmation.company')
            }}</span>
            <span class="summary-value tw:min-w-0 tw:text-right tw:font-medium tw:wrap-anywhere tw:text-gray-900">{{
              companyName
            }}</span>
          </div>

          <!-- a div rather than <hr>, whose global base rule pins the colour with !important -->
          <template v-if="productRows.length">
            <div role="separator" class="summary-divider tw:my-4 tw:border-t tw:border-solid tw:border-border" />
            <div
              v-for="row in productRows"
              :key="row.id"
              data-testid="oc-confirmation-product-row"
              class="summary-row product-row tw:flex tw:items-center tw:justify-between tw:gap-6 tw:py-1.5 tw:text-14">
              <div class="product-info tw:flex tw:items-center tw:gap-3">
                <div
                  class="product-icon tw:flex tw:size-8 tw:shrink-0 tw:items-center tw:justify-center tw:rounded-md tw:border tw:border-solid tw:border-border-secondary tw:bg-surface tw:text-gray-900">
                  <img v-if="row.image" :src="row.image" :alt="row.name" class="tw:size-4.5 tw:object-contain" />
                  <component v-else :is="row.icon" :size="16" :stroke-width="1.75" />
                </div>
                <span class="product-name tw:font-medium tw:text-gray-900">{{ row.name }}</span>
              </div>
              <span class="summary-value tw:min-w-0 tw:text-right tw:font-medium tw:wrap-anywhere tw:text-gray-900">{{
                row.total
              }}</span>
            </div>

            <div role="separator" class="summary-divider tw:my-4 tw:border-t tw:border-solid tw:border-border" />
            <div
              class="summary-row total-row tw:flex tw:items-start tw:justify-between tw:gap-6 tw:py-1.5 tw:text-14 tw:font-bold tw:text-gray-900">
              <span>{{ $t('order_creation.confirmation.total') }}</span>
              <span data-testid="oc-confirmation-total">{{ formattedTotal }}</span>
            </div>
          </template>

          <template v-if="campaignStart">
            <div role="separator" class="summary-divider tw:my-4 tw:border-t tw:border-solid tw:border-border" />
            <div class="summary-row tw:flex tw:items-start tw:justify-between tw:gap-6 tw:py-1.5 tw:text-14">
              <span class="summary-label tw:shrink-0 tw:text-13 tw:text-muted">{{
                $t('order_creation.confirmation.campaign_start')
              }}</span>
              <span
                data-testid="oc-confirmation-campaign-start"
                class="summary-value tw:min-w-0 tw:text-right tw:font-medium tw:wrap-anywhere tw:text-gray-900"
                >{{ campaignStart }}</span
              >
            </div>
          </template>
        </div>

        <div
          v-if="previewLink"
          data-testid="oc-confirmation-preview-link"
          class="tw:mt-6 tw:flex tw:flex-col tw:gap-1.5">
          <span class="preview-link-label tw:text-13 tw:font-medium tw:text-gray-700">{{
            $t('order_creation.confirmation.preview_link')
          }}</span>
          <div class="preview-link-row tw:flex tw:min-w-0 tw:items-center tw:gap-2 tw:text-primary">
            <IconLink :size="16" class="tw:shrink-0 tw:text-gray-400" />
            <a
              :href="previewLink"
              target="_blank"
              rel="noopener"
              class="preview-link-url tw:min-w-0 tw:truncate tw:text-14! tw:font-semibold tw:text-primary!"
              >{{ previewLink }}</a
            >
            <LeadButton
              data-testid="oc-confirmation-copy-preview-link"
              variant="ghost"
              size="sm"
              :icon="isCopied ? IconCheck : IconCopy"
              class="copy-button"
              :class="{ 'tw:text-success-700!': isCopied }"
              :aria-label="$t('order_creation.confirmation.copy')"
              v-tooltip="isCopied ? undefined : $t('order_creation.confirmation.copy')"
              @click="copyPreviewLink" />
            <Transition
              enter-active-class="tw:transition-opacity tw:duration-200"
              leave-active-class="tw:transition-opacity tw:duration-200"
              enter-from-class="tw:opacity-0"
              leave-to-class="tw:opacity-0">
              <span v-if="isCopied" class="tw:text-12 tw:font-medium tw:text-success-700">{{
                $t('order_creation.confirmation.copied')
              }}</span>
            </Transition>
          </div>
        </div>

        <div class="tw:mt-6 tw:flex tw:flex-col tw:items-center tw:gap-3">
          <LeadButton
            data-testid="oc-confirmation-view-order-button"
            variant="primary"
            block
            :trailing-icon="IconArrowRight"
            @click="viewOrder"
            >{{ $t('order_creation.confirmation.view_order') }}</LeadButton
          >
          <LeadButton data-testid="oc-confirmation-place-another-button" variant="link" @click="emit('placeAnother')">{{
            $t('order_creation.confirmation.place_another')
          }}</LeadButton>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { IconArrowRight, IconCheck, IconCopy, IconLink, IconSparkles } from '@tabler/icons-vue';
import translator from '@/locale/translator';
import { useOrderCreationStore } from '@/stores/orderCreation';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';
import { useUserStore } from '@/stores/user';
import { fetchAvailablePreviews, savePreviewOptions } from '@/api/order';
import { convertCurrency } from '@/api/product';
import { formatCurrency } from '@/locale/format';
import { formatDate } from '@/filters/dateFormat';
import { calculateCartTotal, calculateProductTotal, sumCartTotalInCurrency } from '../product/cartCalculations';
import { getCampaignProviderMeta } from './subcomponents/campaignProviderIcons';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import ConfettiBurst from '@/components/generic/ConfettiBurst.vue';
import { getPlacedOrderPreviewLink, setPlacedOrderPreviewLink } from '@/func/placedOrders';

interface Props {
  // false while the page still shows its loader, so the confetti doesn't play behind it
  celebrate?: boolean;
}

const props = withDefaults(defineProps<Props>(), { celebrate: true });
const emit = defineEmits<{ placeAnother: [] }>();

const router = useRouter();
const userStore = useUserStore();
const { featureOrderManagment } = storeToRefs(useCustomerFeaturesStore());
const { order, productsToSave, selectedProducts } = storeToRefs(useOrderCreationStore());

/* ---------------------------------- STATE --------------------------------- */
const formattedTotal = ref('');
const previewLink = ref('');
const isCopied = ref(false);
const showConfetti = ref(false);
let copiedTimer: ReturnType<typeof setTimeout> | null = null;

// decorative dots scattered around the hero icon
const heroDots = [
  { key: 1, top: 4, left: 40, size: 14, colorClass: 'tw:bg-success-200' },
  { key: 2, top: 22, left: 30, size: 9, colorClass: 'tw:bg-gray-300' },
  { key: 3, top: 36, left: 22, size: 10, colorClass: 'tw:bg-orange-200' },
  { key: 4, top: 85, left: 26, size: 11, colorClass: 'tw:bg-primary' },
  { key: 5, top: 0, left: 62, size: 9, colorClass: 'tw:bg-primary' },
  { key: 6, top: 8, left: 74, size: 13, colorClass: 'tw:bg-primary' },
  { key: 7, top: 70, left: 69, size: 13, colorClass: 'tw:bg-orange-200' },
  { key: 8, top: 92, left: 76, size: 9, colorClass: 'tw:bg-success-200' },
].map((dot) => ({
  key: dot.key,
  colorClass: dot.colorClass,
  style: {
    top: `${dot.top}%`,
    left: `${dot.left}%`,
    width: `${dot.size}px`,
    height: `${dot.size}px`,
  },
}));

/* -------------------------------- COMPUTED -------------------------------- */
const subtitle = computed(() =>
  featureOrderManagment.value?.self_service
    ? translator.get('order_creation.confirmation.subtitle.self_service')
    : translator.get('order_creation.confirmation.subtitle.managed')
);

const companyName = computed(() => order.value.company?.data?.name || order.value.company_name);

const productRows = computed(() =>
  productsToSave.value.map((product) => {
    const catalogProduct = selectedProducts.value.find((p) => p.id === product.id);
    const provider = catalogProduct?.campaign_providers?.[0];
    return {
      id: product.id,
      name: product.name || catalogProduct?.name || '',
      // the provider's brand icon when there is one, the product image only as a fallback
      image: provider ? undefined : product.image,
      icon: getCampaignProviderMeta(provider ?? '').icon,
      total: calculateProductTotal(product, selectedProducts.value),
    };
  })
);

// the order's own start date once set, otherwise the earliest product start
const campaignStart = computed(() => {
  const startDates = productsToSave.value.map((product) => product.startdate).filter(Boolean);
  const start = order.value.startdate || startDates.sort()[0];
  return start ? formatDate(start, 'd MMM yyyy') : '';
});

/* --------------------------------- METHODS -------------------------------- */
async function calculateTotal() {
  const targetCurrency = userStore.currentCustomer?.currency || 'CHF';
  try {
    const totals = calculateCartTotal(productsToSave.value, selectedProducts.value);
    const total = await sumCartTotalInCurrency(totals, targetCurrency, convertCurrency);
    formattedTotal.value = formatCurrency(total, targetCurrency);
  } catch (error) {
    console.error('Failed to calculate the order total:', error);
  }
}

// same link as the orders list "Get Preview Link" action, with every available preview included.
// no previews yet means the creatives aren't generated, in which case the section stays hidden.
// without self service the order isn't finalized here, so there are no creatives to preview
async function loadPreviewLink() {
  const { self_service, allow_previews } = featureOrderManagment.value ?? {};
  if (!order.value.id || !self_service || !allow_previews) return;

  // reuse the link generated for this order before a reload instead of minting another one
  const storedLink = getPlacedOrderPreviewLink(order.value.id);
  if (storedLink) {
    previewLink.value = storedLink;
    return;
  }

  try {
    const available = (await fetchAvailablePreviews(order.value.id)) || {};
    const previews: Record<string, string[]> = {};
    for (const [providerKey, provider] of Object.entries(available)) {
      const keys = Object.keys(provider?.previews || {});
      if (keys.length) previews[providerKey] = keys;
    }
    if (!Object.keys(previews).length) return;

    const res = await savePreviewOptions(order.value.id, previews);
    if (res?.url) {
      previewLink.value = res.url;
      setPlacedOrderPreviewLink(order.value.id, res.url);
    }
  } catch (error) {
    console.error('Failed to generate the preview link:', error);
  }
}

async function copyPreviewLink() {
  try {
    await navigator.clipboard.writeText(previewLink.value);
    isCopied.value = true;
    if (copiedTimer) clearTimeout(copiedTimer);
    copiedTimer = setTimeout(() => (isCopied.value = false), 2000);
  } catch (error) {
    console.error('Failed to copy the preview link:', error);
  }
}

function viewOrder() {
  router.push({ name: 'order.detail.info', params: { id: String(order.value.id) } });
}

/* -------------------------------- WATCHERS -------------------------------- */
// latched, so the burst isn't cut off if the flag flips back
watch(
  () => props.celebrate,
  (celebrate) => {
    if (celebrate) showConfetti.value = true;
  },
  { immediate: true }
);

/* ----------------------------- LIFECYCLE HOOKS ---------------------------- */
onMounted(() => {
  calculateTotal();
  loadPreviewLink();
});

onBeforeUnmount(() => {
  if (copiedTimer) clearTimeout(copiedTimer);
});
</script>
